// Group-text deep links. There's no standard for multiple recipients, so each platform's Messages
// app wants its own shape. What's known (researched 2026-10, sources in README):
// - iOS/macOS: `sms://open?addresses=a,b&body=` is the form with recent working reports.
//   `sms:a,b&body=` only fills the FIRST recipient on iOS, so it's deliberately not offered.
// - Android (AOSP Messaging, which Google Messages descends from): recipients are everything
//   before `?`, split on `,` or `;`. The body is percent-decoded twice, so a body must never
//   contain `&` (truncates it), `+` (becomes a space) or `%`. See firstMessage().

const enc = encodeURIComponent

export const SMS_FORMATS = {
  'ios-slashes': {
    label: 'iOS · sms://open?addresses=',
    build: (nums, body) => `sms://open?addresses=${nums.join(',')}${body ? `&body=${enc(body)}` : ''}`,
  },
  ios: {
    label: 'iOS · sms:/open?addresses=',
    build: (nums, body) => `sms:/open?addresses=${nums.join(',')}${body ? `&body=${enc(body)}` : ''}`,
  },
  'ios-pct': {
    label: 'iOS · sms://open with %2B',
    build: (nums, body) => `sms://open?addresses=${nums.map(enc).join(',')}${body ? `&body=${enc(body)}` : ''}`,
  },
  android: {
    label: 'Android · sms:a,b?body=',
    build: (nums, body) => `sms:${nums.join(',')}${body ? `?body=${enc(body)}` : ''}`,
  },
  'android-semi': {
    label: 'Android · sms:a;b?body=',
    build: (nums, body) => `sms:${nums.join(';')}${body ? `?body=${enc(body)}` : ''}`,
  },
  smsto: {
    label: 'Android · smsto:a;b?body=',
    build: (nums, body) => `smsto:${nums.join(';')}${body ? `?body=${enc(body)}` : ''}`,
  },
}

// iPadOS reports itself as Macintosh; macOS Messages also handles sms://open links.
export const isApple = typeof navigator !== 'undefined' && /iPhone|iPad|iPod|Macintosh/.test(navigator.userAgent)
export const isAndroid = typeof navigator !== 'undefined' && /Android/.test(navigator.userAgent)

export const defaultSmsFormat = isApple ? 'ios-slashes' : 'android'

// What "Didn't work?" offers next, most likely first. Unknown platforms get everything.
export const fallbackSmsFormats = isApple
  ? ['ios', 'ios-pct']
  : isAndroid
    ? ['android-semi', 'smsto']
    : Object.keys(SMS_FORMATS).filter((k) => k !== defaultSmsFormat)

export const smsLink = (nums, body, format = defaultSmsFormat) => SMS_FORMATS[format].build(nums, body)

// The first group text. It says who it's from, since nobody has the sender saved yet, and ends
// with the link so Messages shows it as its own preview bubble. Kept free of `&`, `+` and `%`
// because Android decodes the body twice; the link's token is letters and digits only, which
// survives both decodes and every Messages app's link detection. No emoji: one would force UCS-2,
// which more than doubles the SMS segments when a 2-person "group" goes as SMS.
export function firstMessage(contactsUrl, from) {
  const who = from?.replace(/[&+%]/g, ' ').replace(/\s+/g, ' ').trim()
  return `${who ? `Hi, it's ${who}! ` : ''}Here's our group text. Tap to save everyone's number: ${contactsUrl}`
}
